/**
 * Customer Acquisition admin dashboard — lives inside the Marketing hub
 * (`/admin/campaigns?tab=acquisition`) with `embedded` = true.
 *
 * Reads `/api/admin/analytics/customer-acquisition` which now returns a
 * full-funnel payload (visits → signups → billing → first order → paying)
 * so we can answer:
 *   - Where do customers come from?
 *   - Which marketing entry points convert?
 *   - Which sources produce orders & revenue?
 *
 * Uses the shared marketing-visit tracker (`lib/marketingVisit.js`) plus
 * the existing first-touch acquisition capture (`lib/acquisition.js`) —
 * no separate analytics pipeline.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGE_OPTIONS = [
    { value: '7', label: 'Sidste 7 dage' },
    { value: '30', label: 'Sidste 30 dage' },
    { value: '90', label: 'Sidste 90 dage' },
    { value: '180', label: 'Sidste 180 dage' },
    { value: '365', label: 'Sidste år' },
];

const ENTRY_TYPE_LABEL = {
    campaign: 'Campaign',
    landing_page: 'Landing Page',
    affinity: 'Affinity',
};

const ENTRY_TYPE_COLOR = {
    campaign: 'bg-violet-50 text-violet-700 border-violet-200',
    landing_page: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    affinity: 'bg-amber-50 text-amber-700 border-amber-200',
};

function formatEUR(n) {
    const v = Number(n || 0);
    return v.toLocaleString(undefined, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
    });
}

function KpiTile({ label, value, hint, testId, tone = 'default' }) {
    const toneClass = tone === 'accent' ? 'bg-emerald-50 border-emerald-200' : 'bg-white';
    return (
        <div className={`rounded-lg border border-slate-200 ${toneClass} p-4`} data-testid={testId}>
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
            <div className="text-2xl font-semibold text-slate-800 mt-1 tabular-nums">{value}</div>
            {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
        </div>
    );
}

function Funnel({ steps }) {
    const top = Math.max(1, steps[0]?.count || 0);
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700">Funnel</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2" data-testid="funnel-list">
                {steps.map((s, i) => {
                    const raw = top ? (s.count / top) * 100 : 0;
                    const width = Math.max(4, Math.min(100, Math.round(raw)));
                    const rateVsTop = top ? Math.min(100, Math.round(raw)) : 0;
                    return (
                        <div key={s.stage} className="text-xs" data-testid={`funnel-step-${i}`}>
                            <div className="flex justify-between mb-0.5">
                                <span className="text-slate-700 font-medium">{s.stage}</span>
                                <span className="text-slate-500 tabular-nums">
                                    {s.count.toLocaleString()} <span className="text-slate-400">· {rateVsTop}%</span>
                                </span>
                            </div>
                            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                                <div className="h-2 rounded-full bg-emerald-500/80" style={{ width: `${width}%` }} />
                            </div>
                        </div>
                    );
                })}
            </CardContent>
        </Card>
    );
}

function RankedList({ title, items, emptyLabel = 'Ingen data endnu', testId }) {
    const max = Math.max(1, ...items.map(i => i.count || 0));
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700">{title}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
                {items.length === 0 ? (
                    <p className="text-xs text-slate-400" data-testid={`${testId}-empty`}>{emptyLabel}</p>
                ) : (
                    <ul className="space-y-1.5" data-testid={testId}>
                        {items.map((it, i) => (
                            <li key={`${it.value || it.country_code}-${i}`} className="text-xs">
                                <div className="flex justify-between mb-0.5">
                                    <span className="text-slate-700 truncate pr-2">
                                        {it.country_code
                                            ? `${it.country_name || it.country_code} (${it.country_code})`
                                            : it.value}
                                    </span>
                                    <span className="text-slate-500 tabular-nums font-medium">{it.count}</span>
                                </div>
                                <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
                                    <div
                                        className="h-1 rounded-full bg-emerald-400"
                                        style={{ width: `${(it.count / max) * 100}%` }}
                                    />
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </CardContent>
        </Card>
    );
}

const SORT_KEYS = ['visits', 'conversion_pct', 'first_orders', 'revenue_eur'];

function EntryPointTable({ rows }) {
    const [sortKey, setSortKey] = useState('visits');
    const [sortDir, setSortDir] = useState('desc');

    const sorted = useMemo(() => {
        const cp = [...rows];
        cp.sort((a, b) => {
            const av = a[sortKey] || 0;
            const bv = b[sortKey] || 0;
            const cmp = av === bv ? 0 : av > bv ? 1 : -1;
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return cp;
    }, [rows, sortKey, sortDir]);

    const setSort = (k) => {
        if (sortKey === k) {
            setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(k);
            setSortDir('desc');
        }
    };

    const SortIcon = ({ k }) => {
        if (sortKey !== k) return <ArrowUpDown className="h-3 w-3 inline ml-1 text-slate-400" />;
        return sortDir === 'asc'
            ? <ArrowUp className="h-3 w-3 inline ml-1 text-emerald-500" />
            : <ArrowDown className="h-3 w-3 inline ml-1 text-emerald-500" />;
    };

    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700">Entry Point Performance</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="entry-point-table">
                        <thead>
                            <tr className="text-left text-slate-500 border-b border-slate-200">
                                <th className="py-2 pr-3 font-medium">Name</th>
                                <th className="py-2 pr-3 font-medium">Type</th>
                                <th
                                    className="py-2 pr-3 font-medium text-right cursor-pointer select-none hover:text-slate-800"
                                    onClick={() => setSort('visits')}
                                    data-testid="sort-visits"
                                >Visits<SortIcon k="visits" /></th>
                                <th className="py-2 pr-3 font-medium text-right">Signups</th>
                                <th
                                    className="py-2 pr-3 font-medium text-right cursor-pointer select-none hover:text-slate-800"
                                    onClick={() => setSort('conversion_pct')}
                                    data-testid="sort-conversion"
                                >Conv %<SortIcon k="conversion_pct" /></th>
                                <th
                                    className="py-2 pr-3 font-medium text-right cursor-pointer select-none hover:text-slate-800"
                                    onClick={() => setSort('first_orders')}
                                    data-testid="sort-first-orders"
                                >First orders<SortIcon k="first_orders" /></th>
                                <th
                                    className="py-2 pr-3 font-medium text-right cursor-pointer select-none hover:text-slate-800"
                                    onClick={() => setSort('revenue_eur')}
                                    data-testid="sort-revenue"
                                >Revenue<SortIcon k="revenue_eur" /></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="py-6 text-center text-slate-400">
                                        Ingen entry points i denne periode endnu.
                                    </td>
                                </tr>
                            ) : sorted.map((r) => (
                                <tr key={`${r.type}:${r.slug}`} className="border-b border-slate-100 hover:bg-slate-50">
                                    <td className="py-2 pr-3 text-slate-700">
                                        <span className="font-medium">{r.name}</span>
                                        <span className="text-slate-400 ml-1">· /{r.slug}</span>
                                    </td>
                                    <td className="py-2 pr-3">
                                        <Badge variant="outline" className={ENTRY_TYPE_COLOR[r.type] || ''}>
                                            {ENTRY_TYPE_LABEL[r.type] || r.type}
                                        </Badge>
                                    </td>
                                    <td className="py-2 pr-3 text-right tabular-nums">{r.visits.toLocaleString()}</td>
                                    <td className="py-2 pr-3 text-right tabular-nums">{r.signups.toLocaleString()}</td>
                                    <td className="py-2 pr-3 text-right tabular-nums">
                                        {r.visits ? `${r.conversion_pct.toFixed(1)}%` : <span className="text-slate-300">—</span>}
                                    </td>
                                    <td className="py-2 pr-3 text-right tabular-nums">{r.first_orders.toLocaleString()}</td>
                                    <td className="py-2 pr-3 text-right tabular-nums font-medium">
                                        {r.revenue_eur ? formatEUR(r.revenue_eur) : <span className="text-slate-300">—</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className="text-[11px] text-slate-400 mt-2">
                    Revenue in EUR — sum of submitted orders (excludes drafts + quotes).
                    Uses <code className="text-slate-500">order_total_eur</code> stored per order.
                </p>
            </CardContent>
        </Card>
    );
}

function RecentTable({ rows }) {
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700">Seneste signups</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="recent-table">
                        <thead>
                            <tr className="text-left text-slate-500 border-b border-slate-200">
                                <th className="py-2 pr-3 font-medium">Dato</th>
                                <th className="py-2 pr-3 font-medium">Navn</th>
                                <th className="py-2 pr-3 font-medium">Email</th>
                                <th className="py-2 pr-3 font-medium">Entry point</th>
                                <th className="py-2 pr-3 font-medium">Affinity</th>
                                <th className="py-2 pr-3 font-medium">Land</th>
                                <th className="py-2 pr-3 font-medium">Referrer</th>
                                <th className="py-2 pr-3 font-medium">UTM</th>
                                <th className="py-2 pr-3 font-medium">Første ordre</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr><td colSpan={9} className="py-4 text-center text-slate-400">Ingen signups endnu.</td></tr>
                            ) : rows.map(r => (
                                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                                    <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">
                                        {new Date(r.created_at).toLocaleString()}
                                    </td>
                                    <td className="py-2 pr-3 text-slate-700">{r.name || '—'}</td>
                                    <td className="py-2 pr-3 text-slate-600">{r.email}</td>
                                    <td className="py-2 pr-3 text-slate-600">
                                        {r.entry_name ? (
                                            <div className="flex items-center gap-1.5">
                                                <Badge variant="outline" className={`${ENTRY_TYPE_COLOR[r.entry_type] || ''} text-[10px]`}>
                                                    {ENTRY_TYPE_LABEL[r.entry_type] || r.entry_type}
                                                </Badge>
                                                <span className="text-slate-700 truncate">{r.entry_name}</span>
                                            </div>
                                        ) : (
                                            <span className="text-slate-300">—</span>
                                        )}
                                    </td>
                                    <td className="py-2 pr-3 text-slate-600">
                                        {r.affinity_slug || <span className="text-slate-300">—</span>}
                                    </td>
                                    <td className="py-2 pr-3 text-slate-600">
                                        {r.country_name
                                            ? `${r.country_name}${r.city ? ` · ${r.city}` : ''}`
                                            : <span className="text-slate-300">—</span>}
                                    </td>
                                    <td className="py-2 pr-3 text-slate-600">{r.referrer_host || <span className="text-slate-400 italic">Direct</span>}</td>
                                    <td className="py-2 pr-3 text-slate-600">{r.utm_source || <span className="text-slate-300">—</span>}</td>
                                    <td className="py-2 pr-3">
                                        {r.has_paid ? (
                                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Paying</Badge>
                                        ) : r.has_first_order ? (
                                            <Badge variant="outline" className="text-[10px]">Ordered</Badge>
                                        ) : (
                                            <span className="text-slate-300">—</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </CardContent>
        </Card>
    );
}


export default function CustomerAcquisitionPage({ embedded = false } = {}) {
    const [days, setDays] = useState('90');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [backfilling, setBackfilling] = useState(false);

    const fetchData = async (selectedDays) => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/analytics/customer-acquisition`, {
                params: { days: selectedDays },
            });
            setData(data);
        } catch (e) {
            toast.error('Kunne ikke hente customer acquisition data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(days); }, [days]);

    const handleBackfill = async () => {
        setBackfilling(true);
        try {
            const { data } = await axios.post(`${API}/admin/analytics/backfill-geo`);
            toast.success(`Geo-lookup kørt på ${data.processed} signup(s)`);
            await fetchData(days);
        } catch (e) {
            toast.error('Backfill fejlede');
        } finally {
            setBackfilling(false);
        }
    };

    const wrapperClass = embedded
        ? 'space-y-4'
        : 'max-w-7xl mx-auto px-6 py-6';

    const kpis = data?.kpis;

    return (
        <div className={wrapperClass} data-testid="customer-acquisition-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    {!embedded && (
                        <h1 className="text-2xl font-semibold text-slate-800">Customer Acquisition</h1>
                    )}
                    <p className="text-sm text-slate-500 mt-1">
                        Where customers come from, which entry points convert, and revenue they drive.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={days} onValueChange={setDays}>
                        <SelectTrigger className="w-40" data-testid="range-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {RANGE_OPTIONS.map(o => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleBackfill}
                        disabled={backfilling}
                        data-testid="backfill-geo-btn"
                    >
                        {backfilling ? 'Kører...' : 'Backfill geo'}
                    </Button>
                </div>
            </div>

            {loading || !data ? (
                <p className="text-sm text-slate-500">Indlæser...</p>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                        <KpiTile
                            label="Visits"
                            value={(kpis?.visits ?? 0).toLocaleString()}
                            hint={`${(kpis?.unique_visitors ?? 0).toLocaleString()} unique`}
                            testId="kpi-visits"
                        />
                        <KpiTile
                            label="Signups"
                            value={(kpis?.signups ?? 0).toLocaleString()}
                            testId="kpi-signups"
                        />
                        <KpiTile
                            label="Signup conversion"
                            value={
                                (kpis?.visits ?? 0) >= (kpis?.signups ?? 0) && (kpis?.visits ?? 0) > 0
                                    ? `${(kpis?.signup_conversion_pct ?? 0).toFixed(1)}%`
                                    : '—'
                            }
                            hint={
                                (kpis?.visits ?? 0) < (kpis?.signups ?? 0)
                                    ? 'Historical signups exceed tracked visits'
                                    : 'Signups ÷ Visits'
                            }
                            testId="kpi-conversion"
                        />
                        <KpiTile
                            label="First orders"
                            value={(kpis?.first_orders ?? 0).toLocaleString()}
                            hint={`${(kpis?.paying_customers ?? 0).toLocaleString()} paying`}
                            testId="kpi-first-orders"
                        />
                        <KpiTile
                            label="Revenue"
                            value={formatEUR(kpis?.revenue_eur ?? 0)}
                            hint="From acquired customers"
                            testId="kpi-revenue"
                            tone="accent"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
                        <Funnel steps={data.funnel || []} />
                        <div className="md:col-span-2">
                            <EntryPointTable rows={data.entry_points || []} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
                        <RankedList title="Top lande" items={data.countries} testId="list-countries" />
                        <RankedList title="Top henvisere" items={data.top_referrer_hosts} testId="list-referrers" />
                        <RankedList title="Top UTM source" items={data.top_utm_sources} testId="list-utm-sources" />
                    </div>

                    <RecentTable rows={data.recent || []} />
                </>
            )}
        </div>
    );
}
